import { createContext, ReactNode, useContext, useMemo } from "react";
import { useGetList, useGetOne } from "react-admin";
import {
  ClaimItem,
  Claimant,
  Client,
  ObjectType,
  Policy,
  PolicyData,
  Reimbursement,
  ResourceName,
  StoplossClaim,
  Vendor,
} from "@/types";
import {
  DeductibleSource,
  getEffectiveDeductible,
  getResolvedClaimsValues,
  isLaseredDeductible,
} from "@/resources/claimants/hooks/useCurrentPolicyData";
import { isAggregateSpecificPolicy } from "@/resources/clients/components/claims-summary/utils/aggregateSpecific";
import { useClaimReimbursements } from "@/resources/reimbursements/hooks/useClaimReimbursements";
import { getCarrierLabel } from "@/components/common/PolicyField";

/**
 * One set of related reads for the stop-loss claim page (#7581–#7586), shared by
 * the header, the rail, the hold banner and the tabs so the page does not fetch
 * the claimant four times. Every field is optional until its query settles;
 * consumers render what they have.
 *
 * Figures. The claim's `total_paid_amount` is already the netted claim amount
 * (`NetOpenClaimAmount`, orchestrator.go ~2456/2550): spend over the deductible
 * minus the claimant's OTHER stop-loss claims on the SAME policy, every type,
 * vendor and status (`SumStoplossClaimsByClaimantAndPolicy`; ADR-081 D3
 * correction). `total_prior_submissions_ytd` is written at insert only and goes
 * stale on amend, so for an open claim the prior is recomputed live from the
 * sibling claims; a frozen claim shows the stored figure. ASD and family claims
 * use other formulas (carrier payable − prior; pool − family deductible − Σ
 * member priors), so the page does not pretend their ledger adds down.
 */

export interface LineCounts {
  all?: number;
  /** `is_eligible` true: the basis stamp says the line counts. */
  eligible?: number;
  /** `is_eligible` false. Some of these still count (owned by another term, below deductible); see the Lines tab. */
  ineligible?: number;
}

export interface StoplossClaimContextValue {
  claim: StoplossClaim;
  /** The record the claim is shown under: the live survivor for a merged owner (#7244). */
  displayClaimantId?: string;
  claimant?: Claimant;
  client?: Client;
  policy?: Policy;
  /** Administering TPA (ADR-065); undefined when the claim has none. */
  vendor?: Vendor;
  policyData?: PolicyData;
  effectiveDeductible?: { amount: number; label: string; source: DeductibleSource };
  isLasered: boolean;
  reviewPending: boolean;
  policyAccelEnabled: boolean;
  /** Aggregate-specific (ASD) policy: part of the overage is absorbed by the corridor. */
  isASD: boolean;
  /** Policy carries a family deductible endorsement: the claim amount is the family pool's. */
  isFamily: boolean;
  /** Submitted to Aura or the carrier, or no longer `new`: the engine no longer rewrites it. */
  isFrozen: boolean;
  /** Claimant spend on the policy and spend over the deductible, authority-resolved. */
  claimantTotalPaid?: number;
  spendOverDeductible?: number;
  /** The claimant's other claims on this policy (any type, vendor, status), i.e. the netting set. */
  otherClaims: StoplossClaim[];
  otherClaimsCount?: number;
  /** Σ total_paid_amount of the other claims: the prior the engine would net today. */
  priorLive?: number;
  /** total_prior_submissions_ytd as stored at insert. */
  priorStored: number;
  /** What the page shows as "already on other claims": live when open, stored when frozen. */
  priorShown: number;
  /**
   * True when the engine's rule visibly produced this amount: open claim on an
   * individual specific deductible (not ASD, family, contract-laser or excluded)
   * and spend over the deductible − live prior = claim amount to the cent. The
   * rail and Details show the subtraction only then; otherwise today's facts.
   */
  reconciles: boolean;
  reimbursements: Reimbursement[];
  reimbursedTotal: number;
  reimbursementsLoading: boolean;
  reimbursementsError: boolean;
  lineCounts: LineCounts;
  openTaskCount?: number;
}

const StoplossClaimContext = createContext<StoplossClaimContextValue | null>(null);

/**
 * The engine amends only a `new` claim with neither track stamped
 * (`FindOpenStoplossClaimsByClaimantPolicy` / `findOpenSLCForUpdate`; ADR-081
 * 5.6). An in_process or pended claim keeps the amount it has, so the page shows
 * it the stored prior, not a live one (review on !5015).
 */
const OPEN_STATUSES = new Set(["new"]);

/**
 * What the lines on this stop-loss claim are called in the UI: the TPA's medical
 * claims or the PBM's Rx claims. "Claim item" is the storage term and stays in
 * code and routes (PO, 2026-10-27).
 */
export const claimLinesLabel = (claim: Pick<StoplossClaim, "stoploss_claim_type">): string =>
  claim.stoploss_claim_type === "rx"
    ? "Rx claims"
    : claim.stoploss_claim_type === "medical"
      ? "Medical claims"
      : "Medical & Rx claims";

export const isFrozenClaim = (claim: StoplossClaim): boolean =>
  !!claim.carrier_submitted_at || !!claim.aura_submitted_at || !OPEN_STATUSES.has(claim.claim_status);

export const StoplossClaimProvider = ({ claim, children }: { claim: StoplossClaim; children: ReactNode }) => {
  const displayClaimantId = claim.merged_into_claimant_id_ref ?? claim.claimant_id_ref;

  const { data: claimant } = useGetOne<Claimant>(
    ResourceName.CLAIMANTS,
    { id: displayClaimantId ?? "" },
    { enabled: !!displayClaimantId }
  );
  const { data: client } = useGetOne<Client>(
    ResourceName.CLIENTS,
    { id: claim.client_id_ref ?? "" },
    { enabled: !!claim.client_id_ref }
  );
  const { data: policy } = useGetOne<Policy>(
    ResourceName.POLICIES,
    { id: claim.policy_id_ref ?? "" },
    { enabled: !!claim.policy_id_ref }
  );
  const { data: vendor } = useGetOne<Vendor>(
    ResourceName.VENDORS,
    { id: claim.vendor_id_ref ?? "" },
    { enabled: !!claim.vendor_id_ref }
  );

  const policyData = claim.policy_id_ref ? claimant?.policy_data?.[claim.policy_id_ref] : undefined;
  const effective = policyData ? getEffectiveDeductible(policyData, policy) : undefined;
  const claimsValues = getResolvedClaimsValues(policyData);

  const {
    reimbursements,
    reimbursedTotal,
    isLoading: reimbursementsLoading,
    error: reimbursementsError,
  } = useClaimReimbursements(String(claim.id));

  // The netting set: every other claim of this claimant on this policy. No type,
  // vendor or status filter (the engine applies none; a vendor filter would also
  // be ignored by the list API).
  const { data: siblingRows = [] } = useGetList<StoplossClaim>(
    ResourceName.STOPLOSS_CLAIMS,
    {
      filter: { claimant_id_ref: displayClaimantId, policy_id_ref: claim.policy_id_ref },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "created_datetime", order: "ASC" },
    },
    { enabled: !!displayClaimantId && !!claim.policy_id_ref }
  );
  const otherClaims = useMemo(
    () => siblingRows.filter((c) => String(c.id) !== String(claim.id)),
    [siblingRows, claim.id]
  );
  const priorLive = useMemo(
    () => (claim.policy_id_ref ? otherClaims.reduce((s, c) => s + (c.total_paid_amount ?? 0), 0) : undefined),
    [otherClaims, claim.policy_id_ref]
  );
  const priorStored = claim.total_prior_submissions_ytd ?? 0;
  const frozen = isFrozenClaim(claim);

  // Counts come from the server (true totals), not from a loaded page (#7584).
  const lineFilter = { stoploss_claim_id_ref: claim.id };
  const one = { page: 1, perPage: 1 };
  const { total: allLines } = useGetList<ClaimItem>(ResourceName.CLAIM_ITEMS, { filter: lineFilter, pagination: one });
  const { total: eligibleLines } = useGetList<ClaimItem>(ResourceName.CLAIM_ITEMS, {
    filter: { ...lineFilter, is_eligible: true },
    pagination: one,
  });
  const { total: ineligibleLines } = useGetList<ClaimItem>(ResourceName.CLAIM_ITEMS, {
    filter: { ...lineFilter, is_eligible: false },
    pagination: one,
  });

  // Open tasks on this claim, the same filter CompactTaskList uses.
  const { total: openTaskCount } = useGetList(ResourceName.TASKS, {
    filter: {
      "record.object_type": ObjectType.STOPLOSS_CLAIMS,
      "record.object_id": claim.id,
      completed: false,
      deleted: false,
    },
    pagination: one,
  });

  const value = useMemo<StoplossClaimContextValue>(
    () => ({
      claim,
      displayClaimantId,
      claimant,
      client,
      policy,
      vendor,
      policyData,
      effectiveDeductible: effective
        ? { amount: effective.amount, label: effective.label, source: effective.source }
        : undefined,
      isLasered: effective ? isLaseredDeductible(effective, policy) : false,
      reviewPending: !!policyData?.is_deductible_review_pending,
      policyAccelEnabled: Boolean(policy?.is_accel_funding_enabled),
      isASD: isAggregateSpecificPolicy(policy),
      isFamily: Boolean(policy?.has_family_deductible_endorsement),
      isFrozen: frozen,
      claimantTotalPaid: claimsValues?.total_paid,
      spendOverDeductible: claimsValues?.spend_over_deductible,
      otherClaims,
      otherClaimsCount: claim.policy_id_ref ? otherClaims.length : undefined,
      priorLive,
      priorStored,
      priorShown: frozen ? priorStored : (priorLive ?? priorStored),
      reconciles:
        !frozen &&
        !isAggregateSpecificPolicy(policy) &&
        !policy?.has_family_deductible_endorsement &&
        effective?.source !== "CONTRACT" &&
        effective?.source !== "EXCLUDED" &&
        typeof claimsValues?.spend_over_deductible === "number" &&
        typeof priorLive === "number" &&
        Math.abs(claimsValues.spend_over_deductible - priorLive - (claim.total_paid_amount ?? 0)) < 0.01,
      reimbursements,
      reimbursedTotal,
      reimbursementsLoading,
      reimbursementsError,
      lineCounts: { all: allLines, eligible: eligibleLines, ineligible: ineligibleLines },
      openTaskCount,
    }),
    [
      claim,
      displayClaimantId,
      claimant,
      client,
      policy,
      vendor,
      policyData,
      effective,
      claimsValues,
      otherClaims,
      priorLive,
      priorStored,
      frozen,
      reimbursements,
      reimbursedTotal,
      reimbursementsLoading,
      reimbursementsError,
      allLines,
      eligibleLines,
      ineligibleLines,
      openTaskCount,
    ]
  );

  return <StoplossClaimContext.Provider value={value}>{children}</StoplossClaimContext.Provider>;
};

export const useStoplossClaimContext = (): StoplossClaimContextValue => {
  const ctx = useContext(StoplossClaimContext);
  if (!ctx) throw new Error("useStoplossClaimContext must be used inside StoplossClaimProvider");
  return ctx;
};

/** "Summit Re 2026": carrier + policy year, the display convention (never aegle_id). */
export const policyDisplayLabel = (policy?: Policy): string => {
  if (!policy) return "";
  const carrier = getCarrierLabel(policy);
  const start = policy.effective_start_date?.slice(0, 4);
  const end = policy.effective_end_date?.slice(0, 4);
  const years = start && end ? (start === end ? start : `${start}–${end}`) : (start ?? "");
  return [carrier, years].filter(Boolean).join(" ");
};
